/**
 * Program Budget Forecast Service
 * Advanced budget forecasting and analysis
 */
import { apiClient } from './api'

export interface BudgetForecast {
  program_id: number
  forecast_months: number
  forecasted_spending: Array<{
    month: string
    forecasted_amount: number
    confidence: string
  }>
  total_forecasted: number
  average_monthly_burn: number
  confidence: string
}

export interface BurnRate {
  program_id: number
  current_burn_rate: number
  projected_burn_rate: number
  total_budget: number
  total_spent: number
  remaining_budget: number
  period_days: number
  days_until_budget_exhausted: number | null
}

export interface BudgetScenario {
  name: string
  description: string
  monthly_burn_rate: number
  total_6_months: number
  confidence: string
}

export interface BudgetScenarios {
  program_id: number
  scenarios: BudgetScenario[]
  generated_at: string
}

export interface BudgetVariance {
  program_id: number
  planned_budget: number
  actual_spending: number
  variance: number
  variance_percentage: number
  status: string
}

export interface BudgetAtCompletion {
  program_id: number
  budget_at_completion: number
  estimate_at_completion: number
  variance_at_completion: number
  cost_performance_index: number
  earned_value: number
  actual_cost: number
  forecast_status: string
}

export const programBudgetForecastService = {
  /**
   * Get budget forecast
   */
  async getForecast(
    programId: number,
    forecastMonths: number = 6
  ): Promise<BudgetForecast> {
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/budget/forecast`,
      { params: { forecast_months: forecastMonths } }
    )
    return response.data
  },

  /**
   * Get burn rate analysis
   */
  async getBurnRate(
    programId: number,
    periodDays: number = 30
  ): Promise<BurnRate> {
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/budget/burn-rate`,
      { params: { period_days: periodDays } }
    )
    return response.data
  },

  /**
   * Get budget scenarios
   */
  async getScenarios(programId: number): Promise<BudgetScenarios> {
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/budget/scenarios`
    )
    return response.data
  },

  /**
   * Calculate budget forecast
   */
  async calculateForecast(
    programId: number,
    forecastMonths: number = 6
  ): Promise<BudgetForecast> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/budget/forecast/calculate`,
      null,
      { params: { forecast_months: forecastMonths } }
    )
    return response.data
  },

  /**
   * Get budget variance
   */
  async getVariance(programId: number): Promise<BudgetVariance> {
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/budget/variance`
    )
    return response.data
  },

  /**
   * Get budget at completion prediction
   */
  async getBudgetAtCompletion(programId: number): Promise<BudgetAtCompletion> {
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/budget/at-completion`
    )
    return response.data
  },
}

